import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";

/* 里程碑「完成条件」绑定：从本工种关联任务里勾选节点 */
export const MilestoneBindDialog = ({ api, sid, milestone, tasks, zh, onClose, onSaved }) => {
  const [sel, setSel] = useState(() => new Set(milestone?.taskNodes || []));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(new Set(milestone?.taskNodes || [])); }, [milestone]);
  if (!milestone) return null;
  const toggle = (n) => setSel((s) => { const x = new Set(s); x.has(n) ? x.delete(n) : x.add(n); return x; });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/subcontracts/${sid}/stages/${milestone.id}/tasks`, { nodes: [...sel] });
      toast.success(zh ? `已保存完成条件（${data.nodes.length} 个任务）${data.autoCompleted?.length ? " · 条件已满足，里程碑进入完工待检" : ""}` : "Saved");
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ms-bind-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{zh ? "里程碑完成条件" : "Milestone completion tasks"} · {milestone.name}</DialogTitle>
          <DialogDescription className="text-[11px]">{zh ? "勾选的任务全部完成 → 该里程碑自动进入「完工待检」；里程碑「验收关闭」→ 勾选任务自动完成并挂验收资料为证据。" : "When all checked tasks are done the milestone auto-enters 'awaiting inspection'; closing the milestone auto-completes them."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-1 text-xs" data-testid="ms-bind-list">
          {tasks.map((t) => (
            <label key={t.node} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 cursor-pointer ${sel.has(t.node) ? "border-brand-400 bg-brand-50/50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`ms-bind-${t.node}`} data-on={sel.has(t.node) ? 1 : 0}>
              <input type="checkbox" checked={sel.has(t.node)} onChange={() => toggle(t.node)} className="mt-0.5" />
              <span className="min-w-0"><span className="font-mono text-[10px] text-slate-400 mr-1">{t.node}</span><span className="text-slate-800">{zh ? t.title : (t.titleEn || t.title)}</span>
                {["done", "skipped"].includes(t.state) && <span className="ml-1 text-emerald-700">✓</span>}</span>
            </label>
          ))}
          {!tasks.length && <p className="text-slate-400">{zh ? "本工种暂无关联任务，请先在「调整范围」里勾选。" : "No linked tasks yet."}</p>}
        </div>
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button><Button size="sm" disabled={busy} onClick={save} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="ms-bind-save">{zh ? "保存" : "Save"}</Button></div>
      </DialogContent>
    </Dialog>
  );
};

/* 项目级「工种任务范围」覆盖：勾选哪些任务属于本工种（覆盖全局 / 自动推断） */
export const TradeScopeDialog = ({ api, pid, trade, tradeName, allTasks, overrides, zh, onClose, onSaved }) => {
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState(false);
  const inScope = (t) => (map[t.node] !== undefined ? (map[t.node] || []).includes(trade) : t.trades.includes(trade));
  const toggle = (t) => {
    const cur = map[t.node] !== undefined ? (map[t.node] || []) : t.trades;
    const next = cur.includes(trade) ? cur.filter((x) => x !== trade) : [...cur, trade];
    setMap((m) => ({ ...m, [t.node]: next }));
  };
  const reset = (t) => setMap((m) => ({ ...m, [t.node]: null }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/projects/${pid}/trade-task-map`, { map }); toast.success(zh ? "已保存（仅本项目）" : "Saved (this project)"); onSaved?.(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const groups = [];
  allTasks.forEach((t) => { let g = groups.find((x) => x.sub === t.sub); if (!g) { g = { sub: t.sub, subName: t.subName, tasks: [] }; groups.push(g); } g.tasks.push(t); });
  const srcLabel = (t) => (map[t.node] !== undefined ? (zh ? "本次修改" : "edited") : overrides[t.node] !== undefined ? (zh ? "项目覆盖" : "project") : t.source === "global" ? (zh ? "全局覆盖" : "global") : t.source ? (zh ? "自动" : "auto") : "");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="trade-scope-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{zh ? "工种任务范围（本项目）" : "Trade task scope (this project)"} · {tradeName}</DialogTitle>
          <DialogDescription className="text-[11px]">{zh ? "勾选 = 该任务属于本工种，会出现在分包卡「阶段任务链」并可绑定为里程碑完成条件。项目级覆盖优先于全局设置与自动推断；「恢复」清除本项目覆盖。" : "Checked tasks belong to this trade on this project; overrides global / inferred mapping."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto space-y-2 text-xs" data-testid="trade-scope-list">
          {groups.map((g) => (
            <div key={g.sub}>
              <p className="text-[10px] font-semibold text-slate-500 mb-0.5">{g.sub} · {g.subName}</p>
              {g.tasks.map((t) => (
                <div key={t.node} className={`flex items-center gap-2 rounded px-2 py-1 ${inScope(t) ? "bg-brand-50/60" : ""}`} data-testid={`trade-scope-${t.node}`} data-on={inScope(t) ? 1 : 0}>
                  <input type="checkbox" checked={inScope(t)} onChange={() => toggle(t)} data-testid={`trade-scope-cb-${t.node}`} />
                  <span className="font-mono text-[10px] text-slate-400">{t.node}</span>
                  <span className="flex-1 truncate text-slate-800" title={t.title}>{zh ? t.title : (t.titleEn || t.title)}</span>
                  {t.trades.filter((x) => x !== trade).length > 0 && <span className="text-[9px] text-slate-400">{t.trades.filter((x) => x !== trade).join(", ")}</span>}
                  <span className="text-[9px] text-slate-400 w-14 text-right">{srcLabel(t)}</span>
                  {(overrides[t.node] !== undefined || map[t.node] !== undefined) && <button type="button" onClick={() => reset(t)} className="text-[9px] text-brand-600 hover:underline" data-testid={`trade-scope-reset-${t.node}`}>{zh ? "恢复" : "reset"}</button>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button><Button size="sm" disabled={busy || !Object.keys(map).length} onClick={save} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="trade-scope-save">{zh ? "保存" : "Save"}</Button></div>
      </DialogContent>
    </Dialog>
  );
};
